import QRCode from "qrcode";

const BRAND_GREEN = "#0f3d2e";
const BRAND_GOLD = "#000000";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + "…").width > maxWidth) t = t.slice(0, -1);
  return t + "…";
}

/**
 * Compose the AI "in your space" result with an RARE GROUP branded footer bar:
 * logo + product name + price + a QR code linking back to the product.
 * Returns a PNG data URL. All inputs are same-origin/data URLs so the canvas
 * stays untainted and exportable.
 */
export async function buildBrandedImage(resultUrl, { name, price, url, lang }) {
  const base = await loadImage(resultUrl);
  const scale = Math.min(1, 1080 / base.width);
  const W = Math.round(base.width * scale);
  const H = Math.round(base.height * scale);
  const barH = Math.round(W * 0.17);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H + barH;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(base, 0, 0, W, H);
  ctx.fillStyle = BRAND_GREEN;
  ctx.fillRect(0, H, W, barH);

  const pad = Math.round(barH * 0.16);
  const contentH = barH - pad * 2;
  const rtl = lang === "ar";

  const [logo, qr] = await Promise.all([
    loadImage("/icon.png").catch(() => null),
    QRCode.toDataURL(url, { margin: 1, width: 260, color: { dark: BRAND_GREEN, light: "#ffffff" } })
      .then(loadImage).catch(() => null),
  ]);

  const logoSize = contentH;
  const qrSize = contentH;
  const gap = pad;

  // Logo at the start corner, QR at the end corner (mirrored for RTL).
  const logoX = rtl ? W - pad - logoSize : pad;
  const qrX = rtl ? pad : W - pad - qrSize;
  const cornerY = H + pad;

  if (logo) {
    // rounded white plate behind the logo for contrast
    ctx.save();
    ctx.beginPath();
    const r = Math.round(logoSize * 0.22);
    const lx = logoX, ly = cornerY, s = logoSize;
    ctx.moveTo(lx + r, ly);
    ctx.arcTo(lx + s, ly, lx + s, ly + s, r);
    ctx.arcTo(lx + s, ly + s, lx, ly + s, r);
    ctx.arcTo(lx, ly + s, lx, ly, r);
    ctx.arcTo(lx, ly, lx + s, ly, r);
    ctx.closePath();
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.clip();
    ctx.drawImage(logo, logoX, cornerY, logoSize, logoSize);
    ctx.restore();
  }

  if (qr) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(qrX - 4, cornerY - 4, qrSize + 8, qrSize + 8);
    ctx.drawImage(qr, qrX, cornerY, qrSize, qrSize);
  }

  // Text block between logo and QR
  const textLeft = rtl ? (qrX + qrSize + gap) : (logoX + logoSize + gap);
  const textRight = rtl ? (logoX - gap) : (qrX - gap);
  const textW = Math.max(40, textRight - textLeft);
  const textAnchorX = rtl ? textRight : textLeft;

  ctx.direction = rtl ? "rtl" : "ltr";
  ctx.textAlign = rtl ? "right" : "left";
  ctx.textBaseline = "alphabetic";

  const nameSize = Math.round(barH * 0.21);
  const priceSize = Math.round(barH * 0.2);
  const tagSize = Math.round(barH * 0.13);
  const cy = H + barH / 2;

  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${nameSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.fillText(truncate(ctx, name || "RARE GROUP", textW), textAnchorX, cy - nameSize * 0.25);

  ctx.fillStyle = BRAND_GOLD;
  ctx.font = `700 ${priceSize}px system-ui, -apple-system, sans-serif`;
  if (price) ctx.fillText(truncate(ctx, price, textW), textAnchorX, cy + priceSize * 0.95);

  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = `500 ${tagSize}px system-ui, -apple-system, sans-serif`;
  const tag = lang === "ar" ? "شاهد المزيد · rgpro.info" : "Discover more · rgpro.info";
  ctx.fillText(truncate(ctx, tag, textW), textAnchorX, cy + priceSize * 0.95 + tagSize * 1.5);

  return canvas.toDataURL("image/png");
}
